<template>
  <slot name="loading" :loading="loading">
    <div v-if="loading" class="tw-flex tw-items-center tw-justify-center">
      <WdsSpinner indeterminate />
    </div>
  </slot>

  <WdsText v-if="!loading && contextMessage && mappedAndSortedFields.length" secondary>
    {{ contextMessage }}
  </WdsText>

  <div
    v-for="(mappedField, index) in displayedFields"
    :key="mappedField.fieldTyped.id"
    class="tw-space-y-2"
  >
    <CustomField
      :data-test="`custom-field-input-${index}`"
      :field="mappedField.field"
      :model-value="mappedField.fieldValue"
      :existing-files="mappedField.existingFiles"
      :error="errors?.[mappedField.fieldTyped.id]?.value"
      :force-enabled="props.forceEnableInputs"
      :force-disabled="props.disableInputs"
      @update:model-value="(value) => onFieldValueChange(mappedField.fieldTyped.id, value)"
    />
  </div>
</template>

<script setup lang="ts">
import { toRef, computed, Ref } from 'vue'
import { WdsSpinner, WdsText } from '@worksome/wds-components'

import { FragmentType } from '@/gql'
import { CustomFieldTypeValueInput } from '@/gql/graphql'

import CustomField from '@/components/Fieldable/CustomField.vue'
import useFormCustomFieldValues from '@/components/Fieldable/composables/useFormCustomFieldValues'
import {
  FieldableFormCustomFieldFragment,
  FieldableFormCustomFieldValueFragment,
} from '@/components/Fieldable/graphql/fragments'
import { useTranslation } from '@/composables/translation'

const emits = defineEmits(['update:field-values'])
const { $t } = useTranslation()

interface Props {
  fields: FragmentType<typeof FieldableFormCustomFieldFragment>[]
  fieldValues: CustomFieldTypeValueInput[]
  fieldValueFragments?: FragmentType<typeof FieldableFormCustomFieldValueFragment>[]
  loading?: boolean
  errors?: { [key: string]: Ref<string> }
  contextMessage?: string
  forceEnableInputs?: boolean
  /** Locks every input, e.g. while a save is in flight so no edit lands mid-request. */
  disableInputs?: boolean
  disableWorkerInputFields?: boolean
  disableRecruiterInputFields?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errors: undefined,
  fieldValueFragments: () => [],
  forceEnableInputs: false,
  disableInputs: false,
  disableWorkerInputFields: false,
  disableRecruiterInputFields: false,
})

const { mappedAndSortedFields, parseUpdateFieldValues } = useFormCustomFieldValues(
  toRef(props, 'fields'),
  toRef(props, 'fieldValues'),
  toRef(props, 'fieldValueFragments'),
)

const displayedFields = computed(() => {
  let fields = mappedAndSortedFields.value

  if (props.disableWorkerInputFields) {
    fields = fields.filter((mappedField) => !mappedField.fieldTyped.workerInputAllowed)
  }

  if (props.disableRecruiterInputFields) {
    fields = fields.filter((mappedField) => !mappedField.fieldTyped.recruiterInputAllowed)
  }

  return fields
})

const onFieldValueChange = (fieldId: string, value: CustomFieldTypeValueInput) => {
  emits('update:field-values', parseUpdateFieldValues(fieldId, value))
}
</script>
